import {
  ASSETS_TYPES,
  assetsCollectionElement,
  IDeliveryDate,
  IEcommerceQuestionSchema
} from '@aytm/app/swidget/base/questions/Ecommerce/types'
import { IImageAsset } from '../../modules/images/types'

const months: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const days: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const phrases: string[] = ['today', 'tomorrow', 'in 2 days', 'in 3 days', 'in 3+ days']

export const createNumbersArray = (length: number, withNull: boolean) =>
  Array(length)
    .fill(0)
    .map((_, index) => {
      return { value: withNull ? `${index}` : `${index + 1}` }
    })

export function getAssetType(asset: assetsCollectionElement): ASSETS_TYPES | null {
  if (!asset || ('original_url' in asset && asset.original_url)) return ASSETS_TYPES.IMAGE
  if ('video' in asset && asset.video) return ASSETS_TYPES.VIDEO
  if ('file_url' in asset && asset.file_url) return ASSETS_TYPES.MODEL
  return null
}

// Typed dialog family (widget.md §Event Catalog): open_<type>_dialog / close_<type>_dialog.
export const assetDialogEvent = (action: 'open' | 'close', asset: assetsCollectionElement) =>
  `${action}_${getAssetType(asset) ?? ASSETS_TYPES.IMAGE}_dialog`

export function getRandomDate(): IDeliveryDate {
  const randomIndex = Math.floor(Math.random() * phrases.length)
  const phrase = phrases[randomIndex]

  const today = new Date()
  const targetDate = new Date(today)

  switch (phrase) {
    case 'today':
      break
    case 'tomorrow':
      targetDate.setDate(today.getDate() + 1)
      break
    case 'in 2 days':
      targetDate.setDate(today.getDate() + 2)
      break
    case 'in 3 days':
      targetDate.setDate(today.getDate() + 3)
      break
    case 'in 3+ days':
      targetDate.setDate(today.getDate() + 4)
      break
  }

  const formattedDate = `${days[targetDate.getDay()]}, ${months[targetDate.getMonth()]} ${targetDate.getDate()}`

  return {
    phrase,
    date: formattedDate,
    originalDate: targetDate
  }
}

export const getRemainingTime = (): string => {
  const now: Date = new Date()
  const endOfDay: Date = new Date()
  endOfDay.setHours(23, 59, 59, 999)

  const diffMs: number = endOfDay.getTime() - now.getTime()
  const hours: number = Math.floor(diffMs / (1000 * 60 * 60))
  const minutes: number = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  return `${hours} hr ${minutes} min`
}

export const formatStringPrice = (price: string) =>
  price.includes('.') ? price.replace(/,/g, '') : price.replace(/,/g, '.')

export const formatPrice = (price: number, priceMask: string, noRound?: boolean, noPoint?: boolean): string =>
  price != null && price != undefined && price.toFixed && priceMask
    ? priceMask.replace(
        '{{price}}',
        '<ins>' +
          '&zwj;' +
          price.toFixed(2).split('.')[0] +
          '&zwj;' +
          '</ins>' +
          '<sub>' +
          (!noRound && price >= 100 ? '' : (noPoint ? '' : '.') + price.toFixed(2).split('.')[1]) +
          '</sub>'
      )
    : '---'

export const formatPriceWithLocale = (
  price: number,
  priceMask: string,
  locale: string,
  noRound?: boolean,
  noPoint?: boolean
): string =>
  price != null && price != undefined && price.toFixed && priceMask
    ? priceMask.replace(
        '{{price}}',
        '<ins>' +
          '&zwj;' +
          Math.trunc(price).toLocaleString(locale).toString() +
          '&zwj;' +
          '</ins>' +
          '<sub>' +
          (!noRound && price >= 100 ? '' : (noPoint ? '' : '.') + price.toFixed(2).split('.')[1]) +
          '</sub>'
      )
    : '---'

export const getDaysAmount = (timestamps: number) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const date = new Date(timestamps)
  date.setHours(0, 0, 0, 0)
  const diffTime = date.getTime() - today.getTime()
  return Math.floor(diffTime / (1000 * 60 * 60 * 24))
}

export const checkLimits = (
  qnt: number,
  price: number,
  value: number,
  totals: number[],
  schema: IEcommerceQuestionSchema
) => {
  if (schema.max_cart_size && totals[0] + qnt > schema.max_cart_size) return false
  if (schema.max_money_spent && totals[1] + price * qnt > schema.max_money_spent) return false
  if (schema.max_qty_per_product && value + qnt > schema.max_qty_per_product) return false
  return true
}

export const getLocale = (locale: string) => {
  const map = locale.split('_')
  return {
    language: map[0],
    country: map[1],
    domain: [
      'AE',
      'SG',
      'IN',
      'CA',
      'JP',
      'AU',
      'BE',
      'BR',
      'MX',
      'TR',
      'DE',
      'FR',
      'IT',
      'ES',
      'NL',
      'PL',
      'SA',
      'SE',
      'EG'
    ].includes(map[1].toUpperCase())
      ? `${['AU', 'BE', 'BR', 'MX', 'TR'].includes(map[1].toUpperCase()) ? 'com.' : ''}${
          ['JP'].includes(map[1].toUpperCase()) ? 'co.' : ''
        }${map[1]}`
      : 'GB' == map[1]
        ? 'co.uk'
        : 'com'
  }
}

export const replaceTags = (str?: string | null) =>
  (str ?? '') // backend sends null for absent description/feature_bullets; every caller wants '' there
    .replace(/&lt;ul&gt;/g, '<ul>')
    .replace(/&lt;\/ul&gt;/g, '</ul>')
    .replace(/&lt;li&gt;/g, '<li>')
    .replace(/&lt;\/li&gt;/g, '</li>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')

export const applyFilter = (asset: IImageAsset) => asset.width != asset.height

// Amazon/Rainforest import wraps the brand as "Visit the <Brand> Store" or "Brand: <Brand>"; brand cells show just
// the brand. " Store" is optional and stripped only with the prefix — the 40-char server cap truncates long values.
export const cleanBrandTitle = (subTitle: string): string =>
  subTitle
    .replace(/^\s*Visit\s+(?:the\s+)?(.+?)(?:\s+Store)?\s*$/i, '$1')
    .replace(/^\s*Brand:\s*/i, '')
    .trim()
